'use client';

import type { SubmitJoinApplicationServiceRequest } from '@musinsa/services-curator';
import { validateSNSForm } from '@musinsa/services-curator';
import { useCallback, useMemo, useState } from 'react';
import type { FunnelStep } from '../types';
import { FUNNEL_STEPS } from '../types';

const INITIAL_FORM_VALUES: SubmitJoinApplicationServiceRequest = {
  snsAccounts: [
    {
      snsType: null,
      url: '',
    },
  ],
  shopTitle: '',
  agreedTerms: [],
};

export const useJoinFunnel = (initialStep: FunnelStep = FUNNEL_STEPS.SNS) => {
  const [step, setStep] = useState<FunnelStep>(initialStep);
  const [formValues, setFormValues] =
    useState<SubmitJoinApplicationServiceRequest>(INITIAL_FORM_VALUES);
  const [duplicationResults, setDuplicationResults] = useState<
    Record<number, { isDuplicated: boolean | null; isChecking: boolean }>
  >({});

  const updateFormValues = useCallback((values: Partial<SubmitJoinApplicationServiceRequest>) => {
    setFormValues((prev) => ({ ...prev, ...values }));
  }, []);

  const updateDuplicationResult = useCallback(
    (index: number, result: { isDuplicated: boolean | null; isChecking: boolean } | null) => {
      setDuplicationResults((prev) => {
        if (!result) {
          if (!(index in prev)) return prev;
          const next = { ...prev };
          delete next[index];
          return next;
        }

        const next = { ...prev };
        next[index] = result;
        return next;
      });
    },
    [],
  );

  const requiredDuplicateIndexes = useMemo(
    () =>
      formValues.snsAccounts
        .map(({ url }, index) => ({
          index,
          hasUrl: typeof url === 'string' && url.trim().length > 0,
        }))
        .filter(({ hasUrl }) => hasUrl)
        .map(({ index }) => index),
    [formValues.snsAccounts],
  );

  const hasDuplicate = useMemo(
    () =>
      requiredDuplicateIndexes.some((index) => duplicationResults[index]?.isDuplicated === true),
    [duplicationResults, requiredDuplicateIndexes],
  );

  const hasPendingCheck = useMemo(
    () =>
      requiredDuplicateIndexes.some((index) => {
        const result = duplicationResults[index];
        return !result || result.isChecking || result.isDuplicated === null;
      }),
    [duplicationResults, requiredDuplicateIndexes],
  );

  const aggregatedDuplicateState = useMemo(() => {
    if (hasDuplicate) return true;
    if (hasPendingCheck) return null;
    return false;
  }, [hasDuplicate, hasPendingCheck]);

  const duplicateMessage = useMemo(
    () => (hasDuplicate ? '이미 가입된 계정 입니다. SNS 정보를 확인해 주세요.' : ''),
    [hasDuplicate],
  );

  const onNextStep = useCallback(() => {
    if (step < FUNNEL_STEPS.ACCOUNT) {
      setStep((prev) => (prev + 1) as FunnelStep);
    }
  }, [step]);

  const onPrevStep = useCallback(() => {
    if (step > FUNNEL_STEPS.SNS) {
      setStep((prev) => (prev - 1) as FunnelStep);
    }
  }, [step]);

  const canProceedToNext = useCallback(() => {
    switch (step) {
      case FUNNEL_STEPS.SNS:
        return validateSNSForm(formValues) && !hasDuplicate && !hasPendingCheck;
      case FUNNEL_STEPS.ACCOUNT:
        return !!formValues.shopTitle && formValues.agreedTerms.length > 0;
      default:
        return false;
    }
  }, [step, formValues, hasDuplicate, hasPendingCheck]);

  return {
    step,
    formValues,
    updateFormValues,
    onNextStep,
    onPrevStep,
    canProceedToNext,
    isDuplicated: aggregatedDuplicateState,
    duplicateMessage,
    duplicationResultsByIndex: duplicationResults,
    updateDuplicationResult,
  };
};
